import { baseApi } from 'api/baseApi';
import { ITEMS_PER_PAGE } from 'components/AdminTable/pageSize';
import { chunk } from 'lodash';
import type { TTEObject } from '@timeedit/types';
import {
  type TFindObjectsPayload,
  type TObjectFindResponse,
  type TObjectListArg,
  type TObjectSavePayload,
  isObjectFindResponse,
} from './objectApi.types';

// The API rejects larger limits with 400; an omitted limit also means 1000
export const MAX_OBJECTS_PER_REQUEST = 1000;

// Per endpoint rather than on baseApi, so other features keep their behaviour
const LIST_TIMEOUT_MS = 30_000;
export type TListMarker = { newestModified: number; total: number };

const LOOKUP_TIMEOUT_MS = 15_000;

const FIND_URL = '/objects/find';
const LIST_TAG = { type: 'Object', id: 'LIST' } as const;
// Saves and deletes change the cached list rows in place (objectListCache) and only reload the count
const COUNT_TAG = { type: 'Object', id: 'COUNT' } as const;

export const toFindResponse = (raw: unknown): TObjectFindResponse => {
  if (!isObjectFindResponse(raw)) {
    throw new Error(`Unexpected response from ${FIND_URL}`);
  }
  return raw;
};

const uniqueSorted = (ids: readonly number[]) =>
  [...new Set(ids)].sort((a, b) => a - b);

export const objectApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // One-off reads by filter, e.g. a new object by its Ext. Id after a save
    findObjects: build.query<TObjectFindResponse, TFindObjectsPayload>({
      query: (body) => ({
        url: FIND_URL,
        method: 'POST',
        body,
        timeout: LIST_TIMEOUT_MS,
      }),
      transformResponse: toFindResponse,
    }),

    findObjectList: build.infiniteQuery<
      TObjectFindResponse,
      TObjectListArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) =>
          lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: FIND_URL,
        method: 'POST',
        body: { ...queryArg, page: pageParam, limit: ITEMS_PER_PAGE },
        timeout: LIST_TIMEOUT_MS,
      }),
      transformResponse: toFindResponse,
      providesTags: [LIST_TAG],
    }),

    // Total within a filter. limit 1 because the API treats limit 0 as its default (1000 objects)
    countObjects: build.query<number, TObjectListArg>({
      query: (arg) => ({
        url: FIND_URL,
        method: 'POST',
        body: { ...arg, limit: 1, page: 1 },
        timeout: LOOKUP_TIMEOUT_MS,
      }),
      transformResponse: (raw: unknown) => toFindResponse(raw).totalResults,
      providesTags: [COUNT_TAG],
    }),

    // null means the server has no object with this id (it answers 200 with no results)
    getObjectById: build.query<TTEObject | null, number>({
      query: (id) => ({
        url: FIND_URL,
        method: 'POST',
        body: {
          ids: [id],
          includeReferenceFields: true,
          // The API hides template objects unless asked, even when read by id
          includeTemplateObjects: true,
          limit: 1,
        },
        timeout: LOOKUP_TIMEOUT_MS,
      }),
      transformResponse: (raw: unknown) =>
        toFindResponse(raw).results[0] ?? null,
      providesTags: (_result, _error, id) => [{ type: 'Object', id }],
    }),

    getObjectsByIds: build.query<TTEObject[], readonly number[]>({
      async queryFn(ids, _api, _extraOptions, baseQuery) {
        const objects: TTEObject[] = [];
        for (const idChunk of chunk(
          uniqueSorted(ids),
          MAX_OBJECTS_PER_REQUEST
        )) {
          const result = await baseQuery({
            url: FIND_URL,
            method: 'POST',
            body: {
              ids: idChunk,
              includeReferenceFields: true,
              includeTemplateObjects: true,
              limit: idChunk.length,
            },
            timeout: LOOKUP_TIMEOUT_MS,
          });
          if (result.error) return { error: result.error };
          if (!isObjectFindResponse(result.data)) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: `Unexpected response from ${FIND_URL}`,
              },
            };
          }
          objects.push(...result.data.results);
        }
        return { data: objects };
      },
      // The same set of ids shares one cache entry regardless of order or duplicates
      serializeQueryArgs: ({ queryArgs }) => ({ ids: uniqueSorted(queryArgs) }),
      providesTags: (result) =>
        result?.map((object) => ({ type: 'Object' as const, id: object.id })) ??
        [],
    }),

    // Change marker for a filtered list: the newest history.modified (unix seconds, 0 if empty)
    // and the total. A deletion leaves the newest change as it was, so the total is needed too.
    getListMarker: build.query<TListMarker, TFindObjectsPayload>({
      query: (payload) => ({
        url: FIND_URL,
        method: 'POST',
        body: { ...payload, sort: 'DESCMOD', limit: 1, page: 1 },
        timeout: LOOKUP_TIMEOUT_MS,
      }),
      transformResponse: (raw: unknown): TListMarker => {
        const { results, totalResults } = toFindResponse(raw);
        return {
          newestModified: results[0]?.history?.modified ?? 0,
          total: totalResults,
        };
      },
      keepUnusedDataFor: 0,
    }),

    createObject: build.mutation<void, TObjectSavePayload>({
      query: (body) => ({ url: '/objects', method: 'POST', body }),
      transformResponse: () => undefined,
      invalidatesTags: [COUNT_TAG],
    }),

    updateObject: build.mutation<
      void,
      { id: number; body: TObjectSavePayload }
    >({
      query: ({ id, body }) => ({
        url: `/objects/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: () => undefined,
      invalidatesTags: (_result, _error, { id }) => [
        COUNT_TAG,
        { type: 'Object', id },
      ],
    }),

    deleteObject: build.mutation<void, number>({
      query: (id) => ({ url: `/objects/${id}`, method: 'DELETE' }),
      transformResponse: () => undefined,
      invalidatesTags: (_result, _error, id) => [
        COUNT_TAG,
        { type: 'Object', id },
      ],
    }),
  }),
  overrideExisting: false,
});

// The mutations are dispatched from the objectEditor thunks, so no hooks for them
export const {
  useFindObjectListInfiniteQuery,
  useCountObjectsQuery,
  useGetObjectByIdQuery,
  useGetObjectsByIdsQuery,
} = objectApi;
